import React, { useState, useCallback, useRef } from 'react';
// --- external imports ------

import firebase, { persistence } from '~/authentication/lib/firebase';
import { validatePassword, validateEmail, errorText } from '~/authentication/lib/credentialValidation';
import { ERROR_CODES } from '~/authentication/lib/errors';
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH } from '~/authentication/lib/constants-auth';
import useAuthRequest from '~/authentication/lib/useAuthRequest';
import AuthErrorScreen, { useAuthErrorScreen } from '~/authentication/AuthErrorScreen';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import AuthBackdrop from '../AuthBackdrop';
import {
	ModalBackground,
	TitleText,
	FieldLabel,
	InputBackground,
	FieldInput,
	EyeButton,
	ErrorMessage,
	FORM_WIDTH_PROJECTION,
} from '~/authentication/AuthAssets';
import {
	CheckboxBackground,
	CheckboxInput,
	CheckmarkImage,
	RememberMeText,
	ForgotPasswordLink,
	PlayButton,
	PlayButtonText,
	EMAIL_INPUT_PROJECTION,
	PASSWORD_INPUT_PROJECTION,
	CHECKBOX_PROJECTION,
	FORGOT_PASSWORD_PROJECTION
} from './LoginPageAssets';
// --- sibling imports ------

function LoginPage() {
	const [emailError, setEmailError] = useState(null);
	const [passwordError, setPasswordError] = useState(null);
	const [isSessionSaved, setIsSessionSaved] = useState(false);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [passwordValue, setPasswordValue] = useState('');

	const emailRef = useRef(null);
	// Signing in is a two-request flow: Firebase resolving only means the second
	// half — `UserProvider` logging the player into the user system — has started,
	// so the loading state is held until this screen navigates away or errors out.
	const { isProcessing, runRequest, stopProcessing } = useAuthRequest({
		shouldKeepProcessingOnSuccess: true
	});

	const handleLoginError = useCallback((error) => {
		const code = error?.code;

		switch (code) {
		case ERROR_CODES.FIREBASE.LOGIN.INVALID_CREDENTIALS:
			setEmailError('Incorrect email or password.');
			break;
		case ERROR_CODES.FIREBASE.LOGIN.USER_DISABLED:
			setEmailError('This user has been disabled! Please contact your HR manager!');
			break;
		case ERROR_CODES.FIREBASE.LOGIN.USER_NOT_FOUND:
			setEmailError('The user associated with this email was not found!');
			break;
		case ERROR_CODES.FIREBASE.GENERAL.NETWORK_REQUEST_FAILED:
			setEmailError('We could not reach the server. Please try again in a moment.');
			break;
		// The user system's own failures are not handled here: an expired licence or
		// a revoked account is not something a field on this form can express, so
		// `AuthErrorScreen` takes those.
		default:
			console.error('Unhandled login error encountered.');
			setEmailError('Something went wrong. Please try again.');
		}
	}, []);

	// A failure the user system reported after Firebase had already accepted the
	// player reaches this screen through the provider, not through the call below.
	// The hook decides which of those get a screen of their own; a refused
	// credential comes back here, where the form has a field for it.
	const {
		screenError,
		clearScreenError,
		onRetry,
		isRetrying
	} = useAuthErrorScreen({
		onSettled: stopProcessing,
		onCredentialsError: handleLoginError
	});

	const handleOnSubmit = useCallback(async () => {
		const email = emailRef.current?.value ?? '';
		const password = passwordValue;

		const { isValid: isValidEmail, error: validEmailError } = validateEmail(email);
		setEmailError(validEmailError);
		if (!isValidEmail) {
			return;
		}

		const { isValid: isValidPassword, error: validPasswordError } = validatePassword(password);
		setPasswordError(validPasswordError);
		if (!isValidPassword) {
			return;
		}

		try {
			await runRequest(async () => {
				// Awaited: signing in before persistence is applied stores the
				// session against whichever mode happened to still be active.
				await firebase.setPersistence(isSessionSaved ? persistence.LOCAL : persistence.NONE);
				await firebase.signInWithEmailAndPassword(email, password);
			});
		}
		catch (error) {
			// Firebase rejected the credentials themselves, so the message belongs on
			// the field that carries them rather than on a screen of its own.
			handleLoginError(error);
		}
	}, [passwordValue, isSessionSaved, handleLoginError, runRequest]);

	const handleOnEmailBlur = useCallback((e) => {
		const { error } = validateEmail(e.target.value);
		setEmailError(error);
	}, []);

	const handleOnPasswordBlur = useCallback((e) => {
		const { error } = validatePassword(e.target.value);
		setPasswordError(error);
	}, []);

	const handleOnPasswordChange = useCallback((e) => {
		setPasswordValue(e.target.value);
	}, []);

	const handleOnSessionSavedChange = useCallback((e) => {
		setIsSessionSaved(e.target.checked);
	}, []);

	const handleToggleSessionSaved = useCallback(() => {
		setIsSessionSaved((prev) => !prev);
	}, []);

	const handlePasswordVisibilityToggle = useCallback(() => {
		setIsPasswordVisible((prev) => !prev);
	}, []);

	const handleDismissScreenError = useCallback(() => {
		// The form unmounts while the error screen is up, so the email field — which
		// is uncontrolled — comes back empty on its own. The password is held in
		// state that outlives that unmount, so it has to be cleared by hand or the
		// player returns to a blank email above their old password, revealed if the
		// eye was toggled. Whatever sent them here is a reason to start over.
		clearScreenError();
		setPasswordValue('');
		setIsPasswordVisible(false);
		setEmailError(null);
		setPasswordError(null);
	}, [clearScreenError]);

	const shouldUsePasswordType = !isPasswordVisible || passwordValue.length === 0;

	// The form has nothing useful to offer for these, and leaving it on display
	// invites the player to keep submitting a request that cannot succeed.
	if (screenError) {
		return (
			<AuthErrorScreen
				error={screenError}
				onRetry={onRetry}
				onBack={handleDismissScreenError}
				isRetrying={isRetrying}
			/>
		);
	}

	return (
		<AuthBackdrop>
			{isProcessing && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={644}
				y={212}
				projection={FORM_WIDTH_PROJECTION}
			>
				LOG IN
			</TitleText>

			{/* Email */}
			<FieldLabel
				x={644}
				y={340}
				projection={FORM_WIDTH_PROJECTION}
			>
				E-Mail
			</FieldLabel>

			<InputBackground
				x={644}
				y={392}
				projection={EMAIL_INPUT_PROJECTION}
			/>

			<FieldInput
				id="email"
				ref={emailRef}
				x={644}
				y={392}
				projection={EMAIL_INPUT_PROJECTION}
				type="email"
				maxLength={EMAIL_MAX_LENGTH}
				onBlur={handleOnEmailBlur}
				disabled={isProcessing}
				hasError={!!emailError}
				data-testid="login-email-input"
			/>

			{emailError && (
				<ErrorMessage
					x={644}
					y={476}
					projection={FORM_WIDTH_PROJECTION}
					data-testid="first-error"
				>
					{errorText(emailError)}
				</ErrorMessage>
			)}

			{/* Password */}
			<FieldLabel
				x={644}
				y={534}
				projection={FORM_WIDTH_PROJECTION}
			>
				Password
			</FieldLabel>

			<InputBackground
				x={644}
				y={590}
				projection={PASSWORD_INPUT_PROJECTION}
			/>

			<FieldInput
				id="password"
				x={644}
				y={590}
				projection={PASSWORD_INPUT_PROJECTION}
				type={shouldUsePasswordType ? 'password' : 'text'}
				maxLength={PASSWORD_MAX_LENGTH}
				value={passwordValue}
				onChange={handleOnPasswordChange}
				onBlur={handleOnPasswordBlur}
				disabled={isProcessing}
				hasError={!!passwordError}
				data-testid="login-password-input"
			/>

			<EyeButton
				x={1226}
				y={618}
				isActive={isPasswordVisible}
				onClick={handlePasswordVisibilityToggle}
				disabled={isProcessing}
				shouldFadeIn={false}
				data-testid="login-eye-icon"
			/>

			{passwordError && (
				<ErrorMessage
					x={644}
					y={674}
					projection={FORM_WIDTH_PROJECTION}
				>
					{errorText(passwordError)}
				</ErrorMessage>
			)}

			{/* Checkbox */}
			<CheckboxBackground
				x={644}
				y={740.5}
			/>

			<CheckboxInput
				x={644}
				y={740.5}
				type="checkbox"
				onChange={handleOnSessionSavedChange}
				checked={isSessionSaved}
				disabled={isProcessing}
				data-testid="login-remember-me-checkbox"
			/>

			{isSessionSaved && (
				<CheckmarkImage
					x={652}
					y={751}
					shouldFadeIn={false}
					data-testid="login-remember-me-checkmark"
				/>
			)}

			<RememberMeText
				x={693}
				y={740}
				onClick={handleToggleSessionSaved}
			>
				Remember me!
			</RememberMeText>

			<ForgotPasswordLink
				x={644}
				y={805}
				projection={FORGOT_PASSWORD_PROJECTION}
				to="/forgot"
			>
				Forgot password?
			</ForgotPasswordLink>

			{/* Submit */}
			<PlayButton
				x={865}
				y={896}
				onClick={handleOnSubmit}
				disabled={isProcessing}
				shouldFadeIn={false}
				data-testid="login-submit"
			/>

			<PlayButtonText
				x={886}
				y={908}
				shouldFadeIn={false}
				data-testid="login-submit-text"
			>
				Play Traders
			</PlayButtonText>
		</AuthBackdrop>
	);
}

export default LoginPage;
